'use client'

import {
  AnalyticsEntityType,
  AnalyticsSource,
  FormattedMessage,
} from '@b-stock/fe-core/bstock-react'

import { BetterOddsBidderCount } from '@/components/betterOdds/BetterOddsBidderCount'
import { BaseCarousel } from '@/components/common/carousel/BaseCarousel'
import type { ApiCategory } from '@/components/personalizedCategoryCarousel/apiCategories'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import type { SearchResult } from '@/queries/listingSearchQuery'

/**
 * Homepage "Better Odds" carousel: open, same-category listings below their
 * unique-bidder magic number. Purely presentational — eligibility (does the
 * buyer have a losing bid, are there candidates) is decided by
 * BetterOddsCarouselSlot, which mounts this only when both are true and
 * falls back to the normal lead carousel otherwise.
 */
export default function BetterOddsCarousel({
  candidates,
  category,
  isLoading,
}: {
  candidates: SearchResult[]
  category: ApiCategory
  isLoading: boolean
}) {
  const { track } = useAnalytics()

  const handleViewAllClick = () => {
    track('button_click', {
      button_name: 'carousel_view_all',
      carousel_name: 'better_odds',
      screen_name: 'homepage',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
    })
  }

  return (
    <BaseCarousel
      data={{ listings: candidates, total: candidates.length }}
      isLoading={isLoading}
      title={
        <FormattedMessage
          id="Common.Carousel.betterOddsTitle"
          values={{ category }}
        />
      }
      viewMoreUrl={`/all-auctions?category=${encodeURIComponent(category)}&sortBy=numberOfBids&sortOrder=asc`}
      viewMoreText={<FormattedMessage id="Common.Carousel.viewAll" />}
      onViewAllClick={handleViewAllClick}
      carouselName="better_odds"
      showMobileButton
      enableGTMTracking={false}
      renderCardExtra={(listing) => <BetterOddsBidderCount listing={listing} />}
    />
  )
}
